import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';

interface DroneModel {
  id: string;
  manufacturerId: string;
  modelName: string;
  description: string | null;
  maxTakeoffWeightLbs: number | null;
  imageUrl: string | null;
  createdAt: string;
  updatedAt: string;
}

interface DroneManufacturer {
  id: string;
  name: string;
}

@Component({
  selector: 'app-model-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './model-form.component.html',
  styleUrl: './model-form.component.scss'
})
export class ModelFormComponent implements OnInit {
  modelForm: FormGroup;
  loading = false;
  loadingModel = false;
  errorMessage = '';
  successMessage = '';

  isEditMode = false;
  modelId: string | null = null;
  manufacturerId: string | null = null;
  manufacturerName = '';

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private http: HttpClient
  ) {
    this.modelForm = this.fb.group({
      modelName: ['', [Validators.required, Validators.minLength(1)]],
      description: [''],
      maxTakeoffWeightLbs: [null, [Validators.min(0), Validators.max(10000)]],
      imageUrl: ['', [Validators.pattern(/^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([\/\w .-]*)*\/?$/)]]
    });
  }

  ngOnInit(): void {
    this.manufacturerId = this.route.snapshot.paramMap.get('manufacturerId');
    this.modelId = this.route.snapshot.paramMap.get('modelId');
    this.isEditMode = !!this.modelId;

    if (this.manufacturerId) {
      this.loadManufacturer();
    }

    if (this.isEditMode) {
      this.loadModel();
    }
  }

  async loadManufacturer(): Promise<void> {
    try {
      const response = await firstValueFrom(
        this.http.get<{ success: boolean; data: DroneManufacturer }>(
          `${environment.apiUrl}/admin/drone-manufacturers/${this.manufacturerId}`
        )
      );

      if (response.success && response.data) {
        this.manufacturerName = response.data.name;
      }
    } catch (error) {
      console.error('Failed to load manufacturer:', error);
    }
  }

  async loadModel(): Promise<void> {
    this.loadingModel = true;

    try {
      const response = await firstValueFrom(
        this.http.get<{ success: boolean; data: DroneModel }>(
          `${environment.apiUrl}/admin/drone-models/${this.modelId}`
        )
      );

      if (response.success && response.data) {
        const model = response.data;

        this.modelForm.patchValue({
          modelName: model.modelName,
          description: model.description || '',
          maxTakeoffWeightLbs: model.maxTakeoffWeightLbs,
          imageUrl: model.imageUrl || ''
        });
      }
    } catch (error) {
      console.error('Failed to load model:', error);
      this.errorMessage = 'Failed to load model details';
    } finally {
      this.loadingModel = false;
    }
  }

  async onSubmit(): Promise<void> {
    if (this.modelForm.invalid) {
      this.modelForm.markAllAsTouched();
      return;
    }

    this.loading = true;
    this.errorMessage = '';
    this.successMessage = '';

    try {
      const formData = {
        ...this.modelForm.value,
        manufacturerId: this.manufacturerId
      };

      // Convert empty string to null for weight
      if (formData.maxTakeoffWeightLbs === '' || formData.maxTakeoffWeightLbs === null) {
        formData.maxTakeoffWeightLbs = null;
      } else {
        formData.maxTakeoffWeightLbs = parseFloat(formData.maxTakeoffWeightLbs);
      }

      if (this.isEditMode) {
        await firstValueFrom(
          this.http.put(
            `${environment.apiUrl}/admin/drone-models/${this.modelId}`,
            formData
          )
        );

        this.successMessage = 'Model updated successfully';

        setTimeout(() => {
          this.router.navigate(['/drone-catalog', this.manufacturerId]);
        }, 1500);
      } else {
        await firstValueFrom(
          this.http.post<{ success: boolean; data: DroneModel }>(
            `${environment.apiUrl}/admin/drone-models`,
            formData
          )
        );

        this.router.navigate(['/success'], {
          state: {
            entities: [
              {
                type: 'model',
                action: 'created',
                name: formData.modelName,
                // Dynamic — go back to the parent manufacturer's detail page.
                listRoute: `/drone-catalog/${this.manufacturerId}`,
              },
            ],
            message: 'Drone model created successfully',
            details: {
              'Model Name': formData.modelName,
              'Manufacturer': this.manufacturerName,
              'Max Takeoff Weight': formData.maxTakeoffWeightLbs ? `${formData.maxTakeoffWeightLbs} lbs` : 'Not specified'
            },
            createAnotherRoute: `/drone-catalog/${this.manufacturerId}/models/create`
          }
        });
      }
    } catch (error: any) {
      console.error('Save model error:', error);
      this.errorMessage = this.isEditMode
        ? 'Failed to update model. Please try again.'
        : 'Failed to create model. Please try again.';
    } finally {
      this.loading = false;
    }
  }

  cancel(): void {
    this.router.navigate(['/drone-catalog', this.manufacturerId]);
  }

  get pageTitle(): string {
    return this.isEditMode ? 'Edit Drone Model' : 'Add Drone Model';
  }

  get submitButtonText(): string {
    if (this.loading) {
      return this.isEditMode ? 'Saving...' : 'Creating...';
    }
    return this.isEditMode ? 'Save Changes' : 'Create Model';
  }
}
